import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../Overlay";
import { PAGE_STATE } from "../../../constants";
import useCertificates from "../../../hooks/useCertificates";
import { format } from "date-fns";
import Button from "../../common/Button";
import TextArea from "../../common/TextArea";
import { Form, Formik } from "formik";
import Slider from "react-slick";
import { toast } from "react-toastify";

const assetsUri = import.meta.env.ASSETS_URI;

const RejectModal = ({ cert: { certId }, onReturn, onSuccess }) => {
  const { patchCertificate, createCertificateRejections } = useCertificates();

  const handleSubmit = ({ reason }, _actions) => {
    Promise.allSettled([
      createCertificateRejections(certId, { reason }),
      patchCertificate(certId, { status: 3 }),
    ]).then(([rejectResult, patchResult]) => {
      if (
        rejectResult.status !== "fulfilled" ||
        patchResult.status !== "fulfilled"
      ) {
        toast.error("Unable to reject");
        return;
      }

      onSuccess();
    });
  };

  return (
    <Overlay
      style={{
        alignItems: "flex-start",
      }}
      onClose={onReturn}
      content={
        <div className="reason-form">
          <h1 className="reason-form__title">What is the reason?</h1>
          <Formik
            initialValues={{ reason: "" }}
            validate={({ reason }) => {
              if (!reason) {
                return {
                  reason: "Cannot leave blank",
                };
              }
              return {};
            }}
            onSubmit={handleSubmit}
          >
            {() => {
              return (
                <Form>
                  <TextArea name="reason" />
                  <div className="reason-form-cta">
                    <Button
                      label="Return"
                      variant="secondary"
                      onClick={onReturn}
                    />
                    <Button label="Submit" type="submit" />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      }
    />
  );
};

const Certificate = ({ cert: { certId }, onClose }) => {
  const {
    getCertificate,
    getCertificateRejections,
    patchCertificate,
    uploadCertificate,
  } = useCertificates();
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [certificate, setCertificate] = useState(undefined);
  const [toggleRejectReason, setToggleRejectReason] = useState(false);
  const [rejections, setRejections] = useState(undefined);

  useEffect(() => {
    init();
    return () => {};
  }, []);

  const init = async () => {
    const [certResult, rejectionsResult] = await Promise.allSettled([
      getCertificate(certId),
      getCertificateRejections(certId),
    ]);

    if (certResult.status === "fulfilled") {
      setCertificate(certResult.value);
    }

    if (rejectionsResult.status === "fulfilled") {
      setRejections(rejectionsResult.value);
    }

    setPageState(PAGE_STATE.IDLE);
  };

  if (toggleRejectReason) {
    return (
      <RejectModal
        cert={{
          certId,
        }}
        onReturn={() => setToggleRejectReason(false)}
        onSuccess={onClose}
      />
    );
  }

  if (pageState === PAGE_STATE.LOADING || !certificate) {
    return <Overlay content={<>Loading</>} onClose={onClose} />;
  }

  const handleApproveClick = async () => {
    await patchCertificate(certificate._id, { status: 2 });
    onClose();
  };

  const getStatus = () => {
    if (certificate.status === 2) {
      return (
        <label
          className={`admin-verification-user-info-status__value ${`admin-verification-user-info-status__value--2`}`}
        >
          Approved
        </label>
      );
    }

    if (certificate.status === 3) {
      return (
        <label
          className={`admin-verification-user-info-status__value ${`admin-verification-user-info-status__value--3`}`}
        >
          Rejected
        </label>
      );
    }

    return (
      <label
        className={`admin-verification-user-info-status__value ${`admin-verification-user-info-status__value--1`}`}
      >
        Pending
      </label>
    );
  };

  const displayDetail = (title, value) => {
    return (
      <>
        <label className="admin-verification-user-info-details__title">
          {title}
        </label>
        <label className="admin-verification-user-info-details__value">
          {value}
        </label>
      </>
    );
  };

  const { name, issuer, effectiveDate, dueDate, url, data } = certificate;

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <Overlay
      style={{
        alignItems: "flex-start",
      }}
      content={
        <div className="admin-verification-user-info">
          <h2 className="admin-verification-user-info__title">Certification</h2>
          <div className="admin-verification-user-info-status">
            <label className="admin-verification-user-info-status__title">
              Verification status
            </label>
            {getStatus()}
          </div>
          <h3 className="admin-verification-user-info__heading">Information</h3>
          <div className="admin-verification-user-info-details">
            {displayDetail("Name", name)}
            {displayDetail("Issuer", issuer)}
            {displayDetail(
              "Period",
              `${format(new Date(effectiveDate), "MMM yyyy")}${
                dueDate ? ` - ${format(new Date(dueDate), "MMM yyyy")}` : ""
              }`
            )}
            {displayDetail("External Link", url || "-")}
          </div>
          {data && data.file && (
            <div>
              <img
                src={`${assetsUri}/${data.file}`}
                onError={({ target }) => {
                  target.src = data.file;
                }}
                style={{
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  maxWidth: "320px",
                }}
              />
            </div>
          )}
          {rejections?.length > 0 && (
            <>
              <h3 className="admin-verification-user-info__heading">
                Past Rejections
              </h3>
              <Slider {...sliderSettings}>
                {rejections.map((r) => {
                  return (
                    <div key={r?._id}>
                      <div className="admin-verification-rejection">
                        <div className="admin-verification-rejection__reason">
                          {r.reason}
                        </div>
                        <div className="admin-verification-rejection__date">
                          On{" "}
                          {r.createdOn
                            ? format(new Date(r.createdOn), "dd/MM/yyyy HH:mm")
                            : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Slider>
            </>
          )}
          <div className="admin-verification-user-info-ctas">
            {certificate.status === 3 || certificate.status === 1 ? (
              <Button
                label="Reject"
                onClick={() => setToggleRejectReason(true)}
                variant={"secondary"}
              />
            ) : (
              <div />
            )}
            {certificate.status !== 2 && (
              <Button label="Approve" onClick={handleApproveClick} />
            )}
          </div>
        </div>
      }
      onClose={onClose}
    />
  );
};

Certificate.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

Certificate.defaultProps = {
  onClose: () => {},
  onSuccess: () => {},
};

export default Certificate;
